import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CtaNameGeneratorService {
  private readonly translate = inject(TranslateService);

  private readonly manualOverrideMap = new WeakMap<FormGroup<ICtaForm>, boolean>();

  public applyInitialContentName(ctaForm: FormGroup<ICtaForm>, ctasValues: FormGroup<ICtaForm>['value'][]): void {
    const currentName = ctaForm.controls.name.value?.trim();

    if (currentName) {
      return;
    }

    const startIndex = ctasValues.length + 1;
    const uniqueName = this.createUniqueNumberedName(ctaForm, ctasValues, 'Content', startIndex);

    ctaForm.controls.name.setValue(uniqueName);
  }

  public applyPlatformFeatureName(
    ctaForm: FormGroup<ICtaForm>,
    ctasValues: FormGroup<ICtaForm>['value'][],
    platformFeature: EPlatformFeature,
  ): void {
    if (this.hasManualOverride(ctaForm)) {
      return;
    }

    const platformCount = ctasValues.filter((ctaValue) => ctaValue.platformFeature === platformFeature).length;
    const startIndex = platformCount + 1;
    const baseName = this.translate.instant(`shared.platformFeatures.${platformFeature}`);
    const uniqueName = this.createUniqueNumberedName(ctaForm, ctasValues, baseName, startIndex);

    ctaForm.controls.name.setValue(uniqueName);
  }

  public applyTwitchGamingStreamName(ctaForm: FormGroup<ICtaForm>, ctasValues: FormGroup<ICtaForm>['value'][]): void {
    if (this.hasManualOverride(ctaForm)) {
      return;
    }

    const currentName = ctaForm.controls.name.value?.trim() ?? '';

    if (this.isExistingCta(ctaForm, ctasValues) && !this.isTwitchStreamNumberedName(currentName)) {
      this.markManualOverride(ctaForm);

      return;
    }

    const twitchCount = ctasValues.filter(
      (ctaValue) => ctaValue.platformFeature === EPlatformFeature.twitchStream,
    ).length;
    const startIndex = twitchCount;
    const uniqueName = this.createUniqueNumberedName(
      ctaForm,
      ctasValues,
      this.translate.instant(
        'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.deliverableStep.twitchGamingStream',
      ),
      startIndex,
    );

    ctaForm.controls.name.setValue(uniqueName);
  }

  public clearManualOverride(ctaForm: FormGroup<ICtaForm>): void {
    this.manualOverrideMap.delete(ctaForm);
  }

  public hasManualOverride(ctaForm: FormGroup<ICtaForm>): boolean {
    return this.manualOverrideMap.get(ctaForm) ?? false;
  }

  public markManualOverride(ctaForm: FormGroup<ICtaForm>): void {
    this.manualOverrideMap.set(ctaForm, true);
  }

  private isExistingCta(ctaForm: FormGroup<ICtaForm>, ctasValues: FormGroup<ICtaForm>['value'][]): boolean {
    const currentCtaId = ctaForm.controls.id.value;

    return ctasValues.some((ctaValue) => ctaValue.id === currentCtaId);
  }

  private isTwitchStreamNumberedName(name: string): boolean {
    const twitchStreamLabel = this.translate.instant('shared.platformFeatures.twitchStream');
    const escapedLabel = this.escapeRegExp(twitchStreamLabel);
    const pattern = new RegExp(`^${escapedLabel}\\s+\\d+$`);

    return pattern.test(name);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private createUniqueNumberedName(
    ctaForm: FormGroup<ICtaForm>,
    ctasValues: FormGroup<ICtaForm>['value'][],
    baseName: string,
    startIndex: number,
  ): string {
    const currentCtaId = ctaForm.controls.id.value;
    let currentIndex = Math.max(startIndex, 1);
    let uniqueName = `${baseName} ${currentIndex}`;

    while (ctasValues.some((ctaValue) => ctaValue.id !== currentCtaId && (ctaValue.name ?? '').trim() === uniqueName)) {
      currentIndex += 1;
      uniqueName = `${baseName} ${currentIndex}`;
    }

    return uniqueName;
  }
}
